import { useState } from "react";
import { CopyIcon, FileTextIcon, PlusIcon } from "lucide-react";

import { JobEvaluationBuild } from "./JobEvaluationBuild";
import { EmptyState, LoadingIndicator } from "@/components";
import { Button } from "@/components/ui/button";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";

import { useGetEvaluations } from "@/hooks/queries/useGetEvaluations";
import { useUpdateSetJobInternalConfig } from "@/hooks/updateSets/useUpdateSetJobInternalConfig";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { EvaluationBuild, JobInternalConfig } from "@/types";

export const JobEvaluationBuilder = ({
  className,
  companyName,
  jobInternalConfig,
  jobTitle,
}: {
  className: string;
  companyName: string;
  jobInternalConfig?: JobInternalConfig;
  jobTitle: string;
}) => {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const { mutate: updateSetJobInternalConfig, isPending } =
    useUpdateSetJobInternalConfig();
  const { data: allEvaluations = [] } = useGetEvaluations();

  const evaluations = allEvaluations.filter((evaluation) =>
    evaluation.name.toLowerCase().includes(searchValue.toLowerCase()),
  );

  // Show recently created evaluations first.
  const sortedEvaluations = evaluations.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  const handleCreateEvaluation = (evaluation?: EvaluationBuild) => {
    if (!jobInternalConfig) return;
    updateSetJobInternalConfig({
      id: jobInternalConfig.id,
      updateRequest: {
        evaluations: [
          {
            id: crypto.randomUUID(),
            blocks: evaluation?.blocks ?? [],
            createdAt: new Date().toISOString(),
            name: `${companyName} - ${jobTitle} Evaluation`,
            rank: 1,
          },
        ],
      },
    });
  };

  const ExistingEvaluationComponent = () => {
    return (
      <>
        <Button
          className="rounded-full"
          onClick={() => {
            mixpanelUtils.trackButtonClick(
              "Use Existing Evaluation",
              MIXPANEL_SCREENS.JOB,
            );
            setOpen(true);
          }}
          variant="outlineDark"
        >
          <CopyIcon className="h-4 w-4" />
          Use Existing Evaluation
        </Button>
        <CommandDialog
          dialogContentClassName="top-[4%] !translate-y-0 max-h-[92vh] overflow-y-scroll"
          onOpenChange={setOpen}
          open={open}
        >
          <CommandInput
            placeholder="Search evaluations..."
            onValueChange={setSearchValue}
            value={searchValue}
          />
          <CommandList className="max-h-[100vh] overflow-y-scroll">
            <CommandGroup>
              {sortedEvaluations.map((ev: EvaluationBuild) => (
                <CommandItem
                  key={ev.id}
                  onSelect={() => {
                    mixpanelUtils.trackButtonClick(
                      "Select evaluation from list",
                      MIXPANEL_SCREENS.JOB,
                      { value: ev.name },
                    );
                    handleCreateEvaluation(ev);
                  }}
                >
                  {ev.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </CommandDialog>
      </>
    );
  };

  if (isPending) {
    return <LoadingIndicator variant="large" />;
  }

  return jobInternalConfig?.evaluations &&
    jobInternalConfig.evaluations.length > 0 ? (
    <>
      {jobInternalConfig.evaluations.map((evaluation) => (
        <JobEvaluationBuild
          key={evaluation.name}
          className={className}
          evaluation={evaluation}
          jobInternalConfig={jobInternalConfig}
        />
      ))}
    </>
  ) : (
    <EmptyState
      description="Create an evaluation to start building screening criteria for candidate calls."
      handlePrimaryClick={() => {
        mixpanelUtils.trackButtonClick(
          "Create new evaluation",
          MIXPANEL_SCREENS.JOB,
        );
        handleCreateEvaluation();
      }}
      Icon={FileTextIcon}
      PrimaryButtonIcon={PlusIcon}
      primaryButtonName="Create Evaluation"
      SecondaryButtonComponent={ExistingEvaluationComponent}
      title="No Evaluation Yet"
    />
  );
};
